import React, { useState, useEffect, useRef } from "react";
import {
	ChevronLeft,
	ChevronRight,
	ChevronDown,
	X,
	Calendar,
	Filter,
} from "lucide-react";
import {
	CircularProgress,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import { enGB } from "date-fns/locale";

// Part number prefixes for filtering (same as VFF sidebar)
const PART_NO_PREFIXES = [
	"92.",
	"81.",
	"06",
	"04",
	"03",
	"01",
	"07",
	"51.",
	"K",
	"M",
	"82",
	"85",
	"U1.",
];

// Custom CSS for Series Sidebar scrollbar - Always visible when needed.
const serieSidebarScrollbarStyles = `
  .serie-sidebar-content::-webkit-scrollbar {
    width: 14px;
    background: #f8f8f8;
  }
  .serie-sidebar-content::-webkit-scrollbar-track {
    background: #f8f8f8;
    border-radius: 8px;
    border: 1px solid #d8d8d8;
  }
  .serie-sidebar-content::-webkit-scrollbar-thumb {
    background: grey;
    border-radius: 8px;
    border: 2px solid #f8f8f8;
    min-height: 40px;
    transition: all 0.2s ease;
  }
  .serie-sidebar-content::-webkit-scrollbar-thumb:hover {
    background: grey;
    border: 1px solid #f8f8f8;
    cursor: pointer;
  }
  .serie-sidebar-content::-webkit-scrollbar-thumb:active {
    background: grey;
  }
  .serie-sidebar-content::-webkit-scrollbar-corner {
    background: #f8f8f8;
  }
  
  /* Enhanced scrollbar visibility for Firefox */
  .serie-sidebar-content {
    scrollbar-width: auto !important;
    scrollbar-color: grey #f8f8f8 !important;
  }
  
  /* Add scroll indicators */
  .serie-sidebar-scroll-indicator-top,
  .serie-sidebar-scroll-indicator-bottom {
    position: sticky;
    left: 0;
    right: 0;
    height: 8px;
    pointer-events: none;
    z-index: 10;
    transition: opacity 0.3s ease;
  }
  
  .serie-sidebar-scroll-indicator-top {
    top: 0;
  }
  
  .serie-sidebar-scroll-indicator-bottom {
    bottom: 0;
  }
  
  .serie-sidebar-content::before {
    content: '';
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    z-index: 10;
    pointer-events: none;
  }
  
  .serie-sidebar-content::after {
    content: '';
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    z-index: 10;
    pointer-events: none;
  }
`;

// Inject the scrollbar styles into the document head
if (typeof document !== "undefined") {
	const existingStyle = document.getElementById(
		"serie-sidebar-scrollbar-styles",
	);
	if (!existingStyle) {
		const style = document.createElement("style");
		style.id = "serie-sidebar-scrollbar-styles";
		style.textContent = serieSidebarScrollbarStyles;
		document.head.appendChild(style);
	}
}

const SerieSidebar = ({
	onFiltersChange,
	onClearFilters,
	filterOptions = {},
	isLoading = false,
	error = null,
	selectedViewType = "Prototype",
	onViewTypeChange,
	initialFilters = {},
}) => {
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [hasScrollableContent, setHasScrollableContent] = useState(false);
	const contentRef = useRef(null);
	const [openDropdowns, setOpenDropdowns] = useState({
		projectNo: false,
		alg: false,
		changeNo: false,
		programm: false,
		supplier: false,
		responsible: false,
		seTeam: false,
		bza: false,
		a80Status: false,
		e99Status: false,
		p99Status: false,
		qi100Status: false,
		qi500Status: false,
		qi900Status: false,
		lr99Status: false,
		mr99Status: false,
		ppapStatus: false,
		wfIndex: false,
		partStatus: false,
		partNoPrefix: false,
		bttStatus: false,
		wfAlg: false,
		wfStufe: false,
		wfBza: false,
		sofreiErf: false,
		tags: false,
	}); // Use filterOptions from propswith default empty arrays if not provided
	const {
		projectNumbers = [],
		algValues = [],
		changeNumbers = [],
		programmValues = [],
		supplierValues = [],
		responsibleValues = [],
		seTeamValues = [],
		followupDateValues = [],
		bzaValues = [],
		a80StatusValues = [],
		e99StatusValues = [],
		p99StatusValues = [],
		qi100StatusValues = [],
		qi500StatusValues = [],
		qi900StatusValues = [],
		lr99StatusValues = [],
		mr99StatusValues = [],
		ppapStatusValues = [],
		wfIndexValues = [],
		statusToggleValues = [],
		wfAlgValues = [],
		wfStufeValues = [],
		wfBzaValues = [],
		projectAlgMapping = {},
		projectChangeMapping = {},
		programmAlgMapping = {},
		programmChangeMapping = {},
		projectSeTeamMapping = {},
		programmSeTeamMapping = {},
		projectBzaMapping = {},
		programmBzaMapping = {},
		projectPpapMapping = {},
		programmPpapMapping = {},
		projectWfIndexMapping = {},
		programmWfIndexMapping = {},
		projectSupplierMapping = {},
		programmSupplierMapping = {},
		projectResponsibleMapping = {},
		programmResponsibleMapping = {},
		sofreiErfValues = [],
		tagsValues = [],
	} = filterOptions || {};
	// selectedFilters must be declared before the dynamic useMemo/useEffect hooks below
	const [selectedFilters, setSelectedFilters] = useState({
		projectNo: initialFilters.projectNo || [],
		alg: initialFilters.alg || [],
		changeNo: initialFilters.changeNo || [],
		programm: initialFilters.programm || [],
		supplier: initialFilters.supplier || [],
		responsible: initialFilters.responsible || [],
		seTeam: initialFilters.seTeam || [],
		bza: initialFilters.bza || [],
		a80Status: initialFilters.a80Status || [],
		e99Status: initialFilters.e99Status || [],
		p99Status: initialFilters.p99Status || [],
		qi100Status: initialFilters.qi100Status || [],
		qi500Status: initialFilters.qi500Status || [],
		qi900Status: initialFilters.qi900Status || [],
		lr99Status: initialFilters.lr99Status || [],
		mr99Status: initialFilters.mr99Status || [],
		ppapStatus: initialFilters.ppapStatus || [],
		wfIndex: initialFilters.wfIndex || [],
		partStatus: initialFilters.partStatus || [],
		partNoPrefix: initialFilters.partNoPrefix || [],
		bttStatus: initialFilters.bttStatus || [],
		wfAlg: initialFilters.wfAlg || [],
		wfStufe: initialFilters.wfStufe || [],
		wfBza: initialFilters.wfBza || [],
		sofreiErf: initialFilters.sofreiErf || [],
		tags: initialFilters.tags || [],
	});
	// Followup date range state(replaces multi-select dropdown)
	const [followupDateFrom, setFollowupDateFrom] = useState(null);
	const [followupDateTo, setFollowupDateTo] = useState(null);
	// FET / PET year range state
	const [fetFrom, setFetFrom] = useState("");
	const [fetTo, setFetTo] = useState("");
	const [petFrom, setPetFrom] = useState("");
	const [petTo, setPetTo] = useState("");
	// Special Release state
	const [sofreiEndeFrom, setSofreiEndeFrom] = useState(null);
	const [sofreiEndeTo, setSofreiEndeTo] = useState(null);

	// Helper to format date to YYYY-MM-DD for backend
	const formatDateForBackend = (date) => {
		if (!date) return null;
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		return `${year}-${month}-${day}`;
	};
	// ── Dynamic ALG / Change No options based on selected Programm + Project No ──  // Helper to sort string arrays
	const sorted = (arr) =>
		[...arr].sort((a, b) => String(a).localeCompare(String(b)));
	// Helper for numeric-aware sort (ALG values are numeric strings like "1", "10", "20")
	const sortedNumeric = (arr) =>
		[...arr].sort((a, b) => {
			const na = parseInt(a, 10);
			const nb = parseInt(b, 10);
			if (!isNaN(na) && !isNaN(nb)) return na - nb;
			if (!isNaN(na)) return -1;
			if (!isNaN(nb)) return 1;
			return a.localeCompare(b);
		});

	const effectiveAlgValues = React.useMemo(() => {
		const selectedProgramms = selectedFilters.programm || [];
		const selectedProjects = selectedFilters.projectNo || [];

		if (selectedProgramms.length === 0 && selectedProjects.length === 0) {
			return sortedNumeric(algValues); // no parent filter active → show all
		}

		const sets = [];
		if (selectedProgramms.length > 0) {
			const fromProgramm = selectedProgramms.flatMap(
				(p) => programmAlgMapping[p] || [],
			);
			sets.push(new Set(fromProgramm));
		}
		if (selectedProjects.length > 0) {
			const fromProject = selectedProjects.flatMap(
				(p) => projectAlgMapping[p] || [],
			);
			sets.push(new Set(fromProject));
		}

		// Union when both programm and project are selected (show ALGs matching either)
		const union = sets.reduce((acc, s) => new Set([...acc, ...s]), new Set());
		return sortedNumeric([...union]);
	}, [
		selectedFilters.programm,
		selectedFilters.projectNo,
		algValues,
		programmAlgMapping,
		projectAlgMapping,
	]);

	const effectiveChangeNumbers = React.useMemo(() => {
		const selectedProgramms = selectedFilters.programm || [];
		const selectedProjects = selectedFilters.projectNo || [];

		if (selectedProgramms.length === 0 && selectedProjects.length === 0) {
			return changeNumbers; // no parent filter active → show all
		}

		const sets = [];
		if (selectedProgramms.length > 0) {
			const fromProgramm = selectedProgramms.flatMap(
				(p) => programmChangeMapping[p] || [],
			);
			sets.push(new Set(fromProgramm));
		}
		if (selectedProjects.length > 0) {
			const fromProject = selectedProjects.flatMap(
				(p) => projectChangeMapping[p] || [],
			);
			sets.push(new Set(fromProject));
		}

		// Union when both programm and project are selected
		const union = sets.reduce((acc, s) => new Set([...acc, ...s]), new Set());
		return sorted([...union]);
	}, [
		selectedFilters.programm,
		selectedFilters.projectNo,
		changeNumbers,
		programmChangeMapping,
		projectChangeMapping,
	]);

	// When effective ALG options change, drop selected ALG values that are no longer available
	useEffect(() => {
		const validSet = new Set(effectiveAlgValues);
		const currentAlg = selectedFilters.alg || [];
		const stillValid = currentAlg.filter((v) => validSet.has(v));
		if (stillValid.length !== currentAlg.length) {
			setSelectedFilters((prev) => ({ ...prev, alg: stillValid }));
		}
	}, [effectiveAlgValues]); // eslint-disable-line react-hooks/exhaustive-deps
	// When effective Change No options change, drop selected Change No values that are no longer available
	useEffect(() => {
		const validSet = new Set(effectiveChangeNumbers);
		const currentChangeNo = selectedFilters.changeNo || [];
		const stillValid = currentChangeNo.filter((v) => validSet.has(v));
		if (stillValid.length !== currentChangeNo.length) {
			setSelectedFilters((prev) => ({ ...prev, changeNo: stillValid }));
		}
	}, [effectiveChangeNumbers]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Helper to build a dynamic effective list for any field ──────────────────
	const makeEffectiveValues = (allValues, projectMapping, programmMapping) => {
		const safeAll = Array.isArray(allValues) ? allValues : [];
		const selectedProgramms = selectedFilters.programm || [];
		const selectedProjects = selectedFilters.projectNo || [];
		if (selectedProgramms.length === 0 && selectedProjects.length === 0)
			return sorted(safeAll);
		const sets = [];
		if (selectedProgramms.length > 0) {
			sets.push(
				new Set(
					selectedProgramms.flatMap((p) => (programmMapping || {})[p] || []),
				),
			);
		}
		if (selectedProjects.length > 0) {
			sets.push(
				new Set(
					selectedProjects.flatMap((p) => (projectMapping || {})[p] || []),
				),
			);
		}
		const union = sets.reduce((acc, s) => new Set([...acc, ...s]), new Set());
		return sorted([...union]);
	};

	const effectiveSeTeamValues = React.useMemo(
		() =>
			makeEffectiveValues(
				seTeamValues,
				projectSeTeamMapping,
				programmSeTeamMapping,
			),
		[
			selectedFilters.programm,
			selectedFilters.projectNo,
			seTeamValues,
			projectSeTeamMapping,
			programmSeTeamMapping,
		], // eslint-disable-line react-hooks/exhaustive-deps
	);
	const effectiveBzaValues = React.useMemo(
		() => makeEffectiveValues(bzaValues, projectBzaMapping, programmBzaMapping),
		[
			selectedFilters.programm,
			selectedFilters.projectNo,
			bzaValues,
			projectBzaMapping,
			programmBzaMapping,
		], // eslint-disable-line react-hooks/exhaustive-deps
	);
	const effectivePpapValues = React.useMemo(
		() =>
			makeEffectiveValues(
				ppapStatusValues,
				projectPpapMapping,
				programmPpapMapping,
			),
		[
			selectedFilters.programm,
			selectedFilters.projectNo,
			ppapStatusValues,
			projectPpapMapping,
			programmPpapMapping,
		], // eslint-disable-line react-hooks/exhaustive-deps
	);
	const effectiveWfIndexValues = React.useMemo(
		() =>
			makeEffectiveValues(
				wfIndexValues,
				projectWfIndexMapping,
				programmWfIndexMapping,
			),
		[
			selectedFilters.programm,
			selectedFilters.projectNo,
			wfIndexValues,
			projectWfIndexMapping,
			programmWfIndexMapping,
		], // eslint-disable-line react-hooks/exhaustive-deps
	);
	const effectiveSupplierValues = React.useMemo(
		() =>
			makeEffectiveValues(
				supplierValues,
				projectSupplierMapping,
				programmSupplierMapping,
			),
		[
			selectedFilters.programm,
			selectedFilters.projectNo,
			supplierValues,
			projectSupplierMapping,
			programmSupplierMapping,
		], // eslint-disable-line react-hooks/exhaustive-deps
	);
	const effectiveResponsibleValues = React.useMemo(
		() =>
			makeEffectiveValues(
				responsibleValues,
				projectResponsibleMapping,
				programmResponsibleMapping,
			),
		[
			selectedFilters.programm,
			selectedFilters.projectNo,
			responsibleValues,
			projectResponsibleMapping,
			programmResponsibleMapping,
		], // eslint-disable-line react-hooks/exhaustive-deps
	);

	// Drop stale selections when effective options change for each dynamic filter
	const dropStale = (filterKey, effectiveList) => {
		const validSet = new Set(effectiveList);
		const current = selectedFilters[filterKey] || [];
		const stillValid = current.filter((v) => validSet.has(v));
		if (stillValid.length !== current.length) {
			setSelectedFilters((prev) => ({ ...prev, [filterKey]: stillValid }));
		}
	};
	useEffect(() => {
		dropStale("seTeam", effectiveSeTeamValues);
	}, [effectiveSeTeamValues]); // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => {
		dropStale("bza", effectiveBzaValues);
	}, [effectiveBzaValues]); // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => {
		dropStale("ppapStatus", effectivePpapValues);
	}, [effectivePpapValues]); // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => {
		dropStale("wfIndex", effectiveWfIndexValues);
	}, [effectiveWfIndexValues]); // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => {
		dropStale("supplier", effectiveSupplierValues);
	}, [effectiveSupplierValues]); // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => {
		dropStale("responsible", effectiveResponsibleValues);
	}, [effectiveResponsibleValues]); // eslint-disable-line react-hooks/exhaustive-deps
	// ─────────────────────────────────────────────────────────────────────────────

	// Debug log to check if supplierValues are being received
	useEffect(() => {
		console.log(
			"🔍 SerieSidebar received supplierValues:",
			supplierValues?.length || 0,
			supplierValues?.slice(0, 5),
		);
	}, [supplierValues]);

	// Debounced search state for each filter
	const [searchTerms, setSearchTerms] = useState({});
	const [debouncedSearchTerms, setDebouncedSearchTerms] = useState({});

	// Monitor scrollable content
	useEffect(() => {
		const checkScrollableContent = () => {
			if (contentRef.current) {
				const hasScroll =
					contentRef.current.scrollHeight > contentRef.current.clientHeight;
				setHasScrollableContent(hasScroll);
			}
		};

		// Check initially and on window resize
		checkScrollableContent();
		const handleResize = () => checkScrollableContent();
		window.addEventListener("resize", handleResize);

		// Use ResizeObserver if available for better detection
		let resizeObserver;
		if (contentRef.current && window.ResizeObserver) {
			resizeObserver = new ResizeObserver(checkScrollableContent);
			resizeObserver.observe(contentRef.current);
		}

		return () => {
			window.removeEventListener("resize", handleResize);
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
		};
	}, [selectedFilters, isSidebarCollapsed, filterOptions]);

	// Debounce effect for search terms
	useEffect(() => {
		const timeouts = {};
		Object.keys(searchTerms).forEach((key) => {
			timeouts[key] = setTimeout(() => {
				setDebouncedSearchTerms((prev) => ({
					...prev,
					[key]: searchTerms[key],
				}));
			}, 300);
		});

		return () => {
			Object.values(timeouts).forEach((timeout) => clearTimeout(timeout));
		};
	}, [searchTerms]);

	const handleSearchChange = (filterName, value) => {
		setSearchTerms((prev) => ({
			...prev,
			[filterName]: value,
		}));
	};

	// Toggle dropdown open/close
	const toggleDropdown = (dropdownName) => {
		setOpenDropdowns((prev) => {
			const isCurrentlyOpen = prev[dropdownName];
			// Close all dropdowns, then toggle the clicked one
			const allClosed = Object.keys(prev).reduce((acc, key) => {
				acc[key] = false;
				return acc;
			}, {});
			return { ...allClosed, [dropdownName]: !isCurrentlyOpen };
		});
	};
	// Handle filter selection (multi-select)
	const handleFilterToggle = (filterName, value) => {
		setSelectedFilters((prev) => {
			const currentValues = prev[filterName] || [];
			const newValues = currentValues.includes(value)
				? currentValues.filter((v) => v !== value)
				: [...currentValues, value];

			const updatedFilters = {
				...prev,
				[filterName]: newValues,
			};

			return updatedFilters;
		});
	};

	// Handle select all functionality
	const handleSelectAll = (filterName, options) => {
		setSelectedFilters((prev) => ({
			...prev,
			[filterName]: [...options],
		}));
	};

	// Handle deselect all functionality
	const handleDeselectAll = (filterName) => {
		setSelectedFilters((prev) => ({
			...prev,
			[filterName]: [],
		}));
	};

	// Get display text for filter dropdowns
	const getFilterDropdownText = (filterName, options) => {
		const selected = selectedFilters[filterName] || [];
		if (selected.length === 0) {
			const displayNames = {
				projectNo: "Project No",
				alg: "ALG",
				changeNo: "Change No",
				programm: "Programm",
				supplier: "Supplier",
				responsible: "Responsible",
				seTeam: "Se-Team",
				bza: "BZA",
				a80Status: "A80 Status",
				e99Status: "E99 Status",
				p99Status: "P99 Status",
				qi100Status: "QI100 Status",
				qi500Status: "QI500 Status",
				qi900Status: "QI900 Status",
				lr99Status: "LR99 Status",
				mr99Status: "MR99 Status",
				ppapStatus: "PPAP Status",
				wfIndex: "Index Type",
				partStatus: "Part Status",
				partNoPrefix: "Part No Prefix",
				bttStatus: "BTT Status",
				wfAlg: "WF ALG",
				wfStufe: "WF Stufe",
				wfBza: "WF BZA",
				sofreiErf: "Special Release Needed",
				tags: "Tags",
			};
			return `Select ${displayNames[filterName] || filterName}`;
		}
		if (selected.length === 1) {
			return selected[0];
		}
		return `${selected.length} items selected`;
	}; // Close dropdown when clicking outside
	useEffect(() => {
		const closeAllDropdowns = () => {
			setOpenDropdowns({
				projectNo: false,
				alg: false,
				changeNo: false,
				supplier: false,
				programm: false,
				responsible: false,
				seTeam: false,
				bza: false,
				a80Status: false,
				e99Status: false,
				p99Status: false,
				qi100Status: false,
				qi500Status: false,
				qi900Status: false,
				lr99Status: false,
				mr99Status: false,
				ppapStatus: false,
				partStatus: false,
				partNoPrefix: false,
				bttStatus: false,
				wfAlg: false,
				wfStufe: false,
				wfBza: false,
				sofreiErf: false,
				tags: false,
			});
		};

		const handleClickOutside = (event) => {
			// If clicking inside an open dropdown content panel, do nothing
			if (event.target.closest(".dropdown-content")) {
				return;
			}
			// Close all dropdowns when clicking anywhere else
			closeAllDropdowns();
		};

		document.addEventListener("click", handleClickOutside);

		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, []);
	const clearFilters = () => {
		setSelectedFilters({
			projectNo: [],
			alg: [],
			changeNo: [],
			supplier: [],
			programm: [],
			responsible: [],
			seTeam: [],
			bza: [],
			a80Status: [],
			e99Status: [],
			p99Status: [],
			qi100Status: [],
			qi500Status: [],
			qi900Status: [],
			lr99Status: [],
			mr99Status: [],
			ppapStatus: [],
			partStatus: [],
			partNoPrefix: [],
			bttStatus: [],
			wfAlg: [],
			wfStufe: [],
			wfBza: [],
			sofreiErf: [],
			tags: [],
		});
		setFollowupDateFrom(null);
		setFollowupDateTo(null);
		setFetFrom("");
		setFetTo("");
		setPetFrom("");
		setPetTo("");
		setSofreiEndeFrom(null);
		setSofreiEndeTo(null);
		setSearchTerms({});
		setDebouncedSearchTerms({});
		if (onClearFilters) {
			onClearFilters();
		}
	};
	const applyFilters = () => {
		console.log(
			"🚀 SerieSidebar: Applying filters:",
			selectedFilters,
			"with view type:",
			selectedViewType,
		);
		console.log(
			"🚀 SerieSidebar: Selected changeNo filters:",
			selectedFilters.changeNo,
		);

		// Close the modal if it's open
		if (isModalOpen) {
			setIsModalOpen(false);
		}

		if (onFiltersChange) {
			const filtersToApply = {
				...selectedFilters,
				followupDateFrom: formatDateForBackend(followupDateFrom),
				followupDateTo: formatDateForBackend(followupDateTo),
				fetFrom: fetFrom || null,
				fetTo: fetTo || null,
				petFrom: petFrom || null,
				petTo: petTo || null,
				sofreiEndeFrom: formatDateForBackend(sofreiEndeFrom),
				sofreiEndeTo: formatDateForBackend(sofreiEndeTo),
			};
			onFiltersChange(filtersToApply, selectedViewType);
		}
	};
	const toggleModal = () => {
		setIsModalOpen(!isModalOpen);
	};

	// Render the Followup Date section with date range picker (same pattern as VFF)
	const renderFollowupDateSection = () => {
		const pickerSlotProps = (value, onChangeFn, onClearFn, placeholder) => ({
			textField: {
				size: "small",
				placeholder,
				sx: {
					width: "90%",
					"& .MuiInputBase-root": {
						fontSize: "12px",
						height: "32px",
						fontFamily: "MAN EUROPE_Regular",
					},
					"& .MuiInputBase-input": { fontSize: "12px", padding: "6px 8px" },
					"& .MuiIconButton-root": { opacity: 1, visibility: "visible" },
				},
			},
			field: { clearable: true, onClear: onClearFn },
			popper: {
				sx: {
					"& .MuiPaper-root": {
						fontSize: "12px",
						"& .MuiPickersCalendarHeader-root": {
							fontSize: "12px",
							minHeight: "32px",
						},
						"& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
							fontSize: "14px",
							padding: "4px",
							color: "#cc0000",
						},
						"& .MuiPickersDay-root": {
							fontSize: "12px",
							width: "28px",
							height: "28px",
							"&:hover": { backgroundColor: "#cc000020" },
							"&.Mui-selected": { backgroundColor: "#cc0000", color: "white" },
						},
						"& .MuiPickersDay-today": { border: "1px solid #cc0000" },
					},
				},
			},
		});

		return (
			<div style={{ ...styles.filterGroup, marginBottom: "25px" }}>
				<label style={styles.filterLabel}>
					<Calendar
						size={16}
						style={{ marginRight: "8px", verticalAlign: "middle" }}
					/>
					Followup Date
				</label>
				<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
					<div
						style={{
							marginLeft: "10px",
							borderLeft: "2px solid #ccc",
							paddingLeft: "10px",
						}}
					>
						{/* From Date */}
						<div style={{ marginBottom: "12px" }}>
							<label
								style={{
									...styles.filterLabel,
									fontSize: "12px",
									marginBottom: "4px",
								}}
							>
								From Date
							</label>
							<DatePicker
								value={followupDateFrom}
								onChange={(newDate) => setFollowupDateFrom(newDate)}
								slotProps={pickerSlotProps(
									followupDateFrom,
									setFollowupDateFrom,
									() => setFollowupDateFrom(null),
									"Select from date",
								)}
							/>
						</div>
						{/* To Date */}
						<div style={{ marginBottom: "12px" }}>
							<label
								style={{
									...styles.filterLabel,
									fontSize: "12px",
									marginBottom: "4px",
								}}
							>
								To Date
							</label>
							<DatePicker
								value={followupDateTo}
								onChange={(newDate) => setFollowupDateTo(newDate)}
								slotProps={pickerSlotProps(
									followupDateTo,
									setFollowupDateTo,
									() => setFollowupDateTo(null),
									"Select to date",
								)}
							/>
						</div>
						{/* Show selected range summary */}
						{(followupDateFrom || followupDateTo) && (
							<div
								style={{
									fontSize: "11px",
									color: "#666",
									fontFamily: "MAN EUROPE_Regular",
									marginTop: "8px",
									padding: "4px 8px",
									backgroundColor: "#f8f8f8",
									borderRadius: "4px",
								}}
							>
								<div>
									From:{" "}
									{followupDateFrom
										? format(followupDateFrom, "dd/MM/yyyy")
										: "Not set"}
								</div>
								<div>
									To:{" "}
									{followupDateTo
										? format(followupDateTo, "dd/MM/yyyy")
										: "Not set"}
								</div>
							</div>
						)}
					</div>
				</LocalizationProvider>
			</div>
		);
	};
	// Render the Emcos Milestones section with grouped status filters
	const renderEmcosMilestonesSection = () => {
		const statusFilters = [
			{ key: "a80Status", label: "A80 Status", options: a80StatusValues || [] },
			{ key: "e99Status", label: "E99 Status", options: e99StatusValues || [] },
			{ key: "p99Status", label: "P99 Status", options: p99StatusValues || [] },
			{
				key: "qi100Status",
				label: "QI100 Status",
				options: qi100StatusValues || [],
			},
			{
				key: "qi500Status",
				label: "QI500 Status",
				options: qi500StatusValues || [],
			},
			{
				key: "qi900Status",
				label: "QI900 Status",
				options: qi900StatusValues || [],
			},
			{
				key: "lr99Status",
				label: "LR99 Status",
				options: lr99StatusValues || [],
			},
			{
				key: "mr99Status",
				label: "MR99 Status",
				options: mr99StatusValues || [],
			},
		];

		return (
			<div style={{ ...styles.filterGroup, marginBottom: "25px" }}>
				<label style={styles.filterLabel}>Emcos Milestones</label>
				<div
					style={{
						marginLeft: "10px",
						borderLeft: "2px solid #ccc",
						paddingLeft: "10px",
					}}
				>
					{statusFilters.map(({ key, label, options }) => (
						<div key={key} style={{ marginBottom: "12px" }}>
							{renderCustomDropdown(key, options, label)}
						</div>
					))}
				</div>
			</div>
		);
	};
	// Render the Additional Filters Modal
	const renderSofreiEndeSection = () => {
		const pickerSlotProps = (value, onClearFn, placeholder) => ({
			textField: {
				size: "small",
				placeholder,
				sx: {
					width: "90%",
					"& .MuiInputBase-root": {
						fontSize: "12px",
						height: "32px",
						fontFamily: "MAN EUROPE_Regular",
					},
					"& .MuiInputBase-input": { fontSize: "12px", padding: "6px 8px" },
					"& .MuiIconButton-root": { opacity: 1, visibility: "visible" },
				},
			},
			field: { clearable: true, onClear: onClearFn },
			popper: {
				sx: {
					"& .MuiPaper-root": {
						fontSize: "12px",
						"& .MuiPickersCalendarHeader-root": {
							fontSize: "12px",
							minHeight: "32px",
						},
						"& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
							fontSize: "14px",
							padding: "4px",
							color: "#cc0000",
						},
						"& .MuiPickersDay-root": {
							fontSize: "12px",
							width: "28px",
							height: "28px",
							"&:hover": { backgroundColor: "#cc000020" },
							"&.Mui-selected": { backgroundColor: "#cc0000", color: "white" },
						},
						"& .MuiPickersDay-today": { border: "1px solid #cc0000" },
					},
				},
			},
		});

		return (
			<div style={{ ...styles.filterGroup, marginBottom: "25px" }}>
				<label style={styles.filterLabel}>Special Release Until</label>
				<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
					<div
						style={{
							marginLeft: "10px",
							borderLeft: "2px solid #ccc",
							paddingLeft: "10px",
						}}
					>
						<div style={{ marginBottom: "12px" }}>
							<label
								style={{
									...styles.filterLabel,
									fontSize: "12px",
									marginBottom: "4px",
								}}
							>
								From Date
							</label>
							<DatePicker
								value={sofreiEndeFrom}
								onChange={(newDate) => setSofreiEndeFrom(newDate)}
								slotProps={pickerSlotProps(
									sofreiEndeFrom,
									() => setSofreiEndeFrom(null),
									"Select from date",
								)}
							/>
						</div>
						<div style={{ marginBottom: "12px" }}>
							<label
								style={{
									...styles.filterLabel,
									fontSize: "12px",
									marginBottom: "4px",
								}}
							>
								To Date
							</label>
							<DatePicker
								value={sofreiEndeTo}
								onChange={(newDate) => setSofreiEndeTo(newDate)}
								slotProps={pickerSlotProps(
									sofreiEndeTo,
									() => setSofreiEndeTo(null),
									"Select to date",
								)}
							/>
						</div>
						{(sofreiEndeFrom || sofreiEndeTo) && (
							<div
								style={{
									fontSize: "11px",
									color: "#666",
									fontFamily: "MAN EUROPE_Regular",
									marginTop: "8px",
									padding: "4px 8px",
									backgroundColor: "#f8f8f8",
									borderRadius: "4px",
								}}
							>
								<div>
									From:{" "}
									{sofreiEndeFrom
										? format(sofreiEndeFrom, "dd/MM/yyyy")
										: "Not set"}
								</div>
								<div>
									To:{" "}
									{sofreiEndeTo
										? format(sofreiEndeTo, "dd/MM/yyyy")
										: "Not set"}
								</div>
							</div>
						)}
					</div>
				</LocalizationProvider>
			</div>
		);
	};

	const renderAdditionalFiltersModal = () => {
		return (
			<Dialog
				open={isModalOpen}
				onClose={toggleModal}
				maxWidth="lg"
				fullWidth
				disableEnforceFocus
				PaperProps={{
					"data-tour-target": "additional-filters-modal",
					sx: {
						fontFamily: "MAN EUROPE_Regular",
						maxHeight: "90vh",
						borderRadius: "0px",
					},
				}}
			>
				<DialogTitle
					sx={{
						fontFamily: "MAN EUROPE_Bold",
						fontSize: "20px",
						color: "#1e2832",
						borderBottom: "2px solid #cc0000",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						pb: 2,
					}}
				>
					Additional Filters
					<Button
						onClick={toggleModal}
						data-tour-target="additional-filters-close"
						sx={{
							minWidth: "auto",
							p: 1,
							color: "#666",
							"&:hover": {
								backgroundColor: "#f5f5f5",
							},
						}}
					>
						<X size={20} />
					</Button>
				</DialogTitle>{" "}
				<DialogContent sx={{ p: 0, overflowY: "auto" }}>
					<div style={styles.modalContent}>
						{" "}
						<div style={styles.modalFilterGrid}>
							{/* Single Column for Filters */}
							<div>
								{" "}
								{/* BTT Status - standalone filter */}
								<div style={styles.modalFilterSection}>
									<div style={{ marginBottom: "12px" }}>
										{renderCustomDropdown(
											"bttStatus",
											[
												"-",
												"Request SF",
												"MET Late",
												"Risk PET",
												"Deleted",
												"AC",
											],
											"BTT Status",
										)}
									</div>
								</div>{" "}
								{/* PPAP Status - dynamic filter */}
								<div style={styles.modalFilterSection}>
									<div style={{ marginBottom: "12px" }}>
										{renderCustomDropdown(
											"ppapStatus",
											effectivePpapValues,
											"PPAP Status",
											true,
										)}
									</div>
								</div>{" "}
								{/* Part Status - standalone filter */}
								<div style={styles.modalFilterSection}>
									<div style={{ marginBottom: "12px" }}>
										{renderCustomDropdown(
											"partStatus",
											["(Blank)", ...(statusToggleValues || [])],
											"Part Status",
										)}
									</div>
								</div>
								{/* Index Type - dynamic filter */}
								<div style={styles.modalFilterSection}>
									<div style={{ marginBottom: "12px" }}>
										{renderCustomDropdown(
											"wfIndex",
											effectiveWfIndexValues,
											"Index Type",
											true,
										)}
									</div>
								</div>{" "}
								{/* Supplier - dynamic filter */}
								<div style={styles.modalFilterSection}>
									<div style={{ marginBottom: "12px" }}>
										{renderCustomDropdown(
											"supplier",
											effectiveSupplierValues,
											"Supplier",
											true,
										)}
									</div>
								</div>{" "}
								{/* Responsible - dynamic filter */}
								<div style={styles.modalFilterSection}>
									<div style={{ marginBottom: "12px" }}>
										{renderCustomDropdown(
											"responsible",
											effectiveResponsibleValues,
											"Responsible",
											true,
										)}
									</div>
								</div>
								{/* Special Release Needed */}
								<div style={styles.modalFilterSection}>
									<div style={{ marginBottom: "12px" }}>
										{renderCustomDropdown(
											"sofreiErf",
											sofreiErfValues,
											"Special Release Needed",
										)}
									</div>
								</div>
								{/* Tags filter */}
								<div style={styles.modalFilterSection}>
									<div style={{ marginBottom: "12px" }}>
										{renderCustomDropdown(
											"tags",
											[...(tagsValues || []), "(Blank)"],
											"Tags",
										)}
									</div>
								</div>
								{/* Special Release Until - date range picker */}
								<div style={styles.modalFilterSection}>
									<div style={{ marginBottom: "12px" }}>
										{renderSofreiEndeSection()}
									</div>
								</div>
								{/* Followup Date- date range picker */}
								<div style={styles.modalFilterSection}>
									<div style={{ marginBottom: "12px" }}>
										{renderFollowupDateSection()}
									</div>
								</div>{" "}
								{/* Emcos Milestones section */}
								<div style={styles.modalFilterSection}>
									{renderEmcosMilestonesSection()}
								</div>{" "}
								{/* FET / PET Year Range section */}
								<div style={styles.modalFilterSection}>
									<label
										style={{ ...styles.filterLabel, marginBottom: "12px" }}
									>
										FET / PET Range
									</label>
									<div
										style={{
											marginLeft: "10px",
											borderLeft: "2px solid #ccc",
											paddingLeft: "10px",
										}}
									>
										{/* FET */}
										<div style={{ marginBottom: "16px" }}>
											<label
												style={{
													...styles.filterLabel,
													fontSize: "12px",
													marginBottom: "4px",
												}}
											>
												FET (Ziel FET)
											</label>
											<div
												style={{
													display: "flex",
													gap: "6px",
													alignItems: "flex-end",
												}}
											>
												<div style={{ flex: 1 }}>
													<label
														style={{
															fontSize: "11px",
															color: "#666",
															display: "block",
															marginBottom: "2px",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														From
													</label>
													<input
														type="text"
														placeholder="e.g. 202501"
														value={fetFrom}
														onChange={(e) => setFetFrom(e.target.value)}
														style={{
															width: "100%",
															padding: "7px 6px",
															border: `1px solid ${fetFrom ? "#cc0000" : "#ddd"}`,
															borderRadius: "4px",
															fontSize: "13px",
															fontFamily: "MAN EUROPE_Regular",
															boxSizing: "border-box",
															outline: "none",
														}}
													/>
												</div>
												<div style={{ flex: 1 }}>
													<label
														style={{
															fontSize: "11px",
															color: "#666",
															display: "block",
															marginBottom: "2px",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														To
													</label>
													<input
														type="text"
														placeholder="e.g. 202512"
														value={fetTo}
														onChange={(e) => setFetTo(e.target.value)}
														style={{
															width: "100%",
															padding: "7px 6px",
															border: `1px solid ${fetTo ? "#cc0000" : "#ddd"}`,
															borderRadius: "4px",
															fontSize: "13px",
															fontFamily: "MAN EUROPE_Regular",
															boxSizing: "border-box",
															outline: "none",
														}}
													/>
												</div>
											</div>
											{(fetFrom || fetTo) && (
												<button
													onClick={() => {
														setFetFrom("");
														setFetTo("");
													}}
													style={{
														marginTop: "4px",
														background: "none",
														border: "none",
														color: "#cc0000",
														fontSize: "11px",
														cursor: "pointer",
														fontFamily: "MAN EUROPE_Regular",
														padding: "0",
													}}
												>
													✕ Clear FET range
												</button>
											)}
										</div>
										{/* PET */}
										<div style={{ marginBottom: "12px" }}>
											<label
												style={{
													...styles.filterLabel,
													fontSize: "12px",
													marginBottom: "4px",
												}}
											>
												PET (Ziel PET)
											</label>
											<div
												style={{
													display: "flex",
													gap: "6px",
													alignItems: "flex-end",
												}}
											>
												<div style={{ flex: 1 }}>
													<label
														style={{
															fontSize: "11px",
															color: "#666",
															display: "block",
															marginBottom: "2px",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														From
													</label>
													<input
														type="text"
														placeholder="e.g. 202501"
														value={petFrom}
														onChange={(e) => setPetFrom(e.target.value)}
														style={{
															width: "100%",
															padding: "7px 6px",
															border: `1px solid ${petFrom ? "#cc0000" : "#ddd"}`,
															borderRadius: "4px",
															fontSize: "13px",
															fontFamily: "MAN EUROPE_Regular",
															boxSizing: "border-box",
															outline: "none",
														}}
													/>
												</div>
												<div style={{ flex: 1 }}>
													<label
														style={{
															fontSize: "11px",
															color: "#666",
															display: "block",
															marginBottom: "2px",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														To
													</label>
													<input
														type="text"
														placeholder="e.g. 202512"
														value={petTo}
														onChange={(e) => setPetTo(e.target.value)}
														style={{
															width: "100%",
															padding: "7px 6px",
															border: `1px solid ${petTo ? "#cc0000" : "#ddd"}`,
															borderRadius: "4px",
															fontSize: "13px",
															fontFamily: "MAN EUROPE_Regular",
															boxSizing: "border-box",
															outline: "none",
														}}
													/>
												</div>
											</div>
											{(petFrom || petTo) && (
												<button
													onClick={() => {
														setPetFrom("");
														setPetTo("");
													}}
													style={{
														marginTop: "4px",
														background: "none",
														border: "none",
														color: "#cc0000",
														fontSize: "11px",
														cursor: "pointer",
														fontFamily: "MAN EUROPE_Regular",
														padding: "0",
													}}
												>
													✕ Clear PET range
												</button>
											)}
										</div>
									</div>
								</div>
								{/* WF Filters section */}
								<div style={styles.modalFilterSection}>
									<label
										style={{ ...styles.filterLabel, marginBottom: "12px" }}
									>
										WF Filters
									</label>
									<div
										style={{
											marginLeft: "10px",
											borderLeft: "2px solid #ccc",
											paddingLeft: "10px",
										}}
									>
										<div style={{ marginBottom: "12px" }}>
											{renderCustomDropdown("wfAlg", wfAlgValues, "WF ALG")}
										</div>
										<div style={{ marginBottom: "12px" }}>
											{renderCustomDropdown(
												"wfStufe",
												wfStufeValues,
												"WF Stufe",
											)}
										</div>
										<div style={{ marginBottom: "12px" }}>
											{renderCustomDropdown("wfBza", wfBzaValues, "WF BZA")}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</DialogContent>
				<DialogActions
					sx={{
						p: 3,
						borderTop: "1px solid #eee",
						gap: 2,
						justifyContent: "center",
					}}
				>
					<Button
						onClick={applyFilters}
						variant="contained"
						sx={{
							backgroundColor: "#cc0000",
							fontFamily: "MAN EUROPE_Bold",
							borderRadius: "0px",
							"&:hover": {
								backgroundColor: "#aa0000",
							},
							px: 4,
							textTransform: "none",
						}}
					>
						Apply Filters
					</Button>
					<Button
						onClick={clearFilters}
						variant="outlined"
						sx={{
							borderColor: "#1e2832",
							borderRadius: "0px",
							color: "white",
							fontFamily: "MAN EUROPE_Bold",
							backgroundColor: "#1e2832",
							"&:hover": {
								backgroundColor: "#1e2832",
								borderColor: "#1e2832",
							},
							px: 4,
							textTransform: "none",
						}}
					>
						Clear all Filters
					</Button>
				</DialogActions>
			</Dialog>
		);
	};
	const renderCustomDropdown = (
		filterName,
		options,
		label,
		isDynamic = false,
	) => {
		const safeOptions = Array.isArray(options) ? options : [];
		const searchTerm = debouncedSearchTerms[filterName] || "";
		const filteredOptions = safeOptions.filter(
			(option) =>
				option != null &&
				String(option).toLowerCase().includes(searchTerm.toLowerCase()),
		);

		const selectedCount = (selectedFilters[filterName] || []).length;
		const isAllSelected =
			selectedCount === filteredOptions.length && filteredOptions.length > 0; // For dynamic filters, build a human-readable "filtered by …" hint matching VFF style
		const hasProgramm =
			isDynamic && (selectedFilters.programm || []).length > 0;
		const hasProjectNo =
			isDynamic && (selectedFilters.projectNo || []).length > 0;
		const filterByText = isDynamic
			? hasProgramm && hasProjectNo
				? "(filtered by programm & project no)"
				: hasProgramm
					? "(filtered by programm)"
					: hasProjectNo
						? "(filtered by project no)"
						: null
			: null;

		return (
			<div key={filterName} style={styles.filterGroup}>
				{" "}
				<label
					style={{
						...styles.filterLabel,
						display: "flex",
						alignItems: "center",
						flexWrap: "wrap",
						gap: "4px",
					}}
				>
					{label}{" "}
					{filterByText && (
						<Filter
							style={{
								width: "12px",
								height: "12px",
								color: "#cc0000",
								marginLeft: "4px",
								flexShrink: 0,
							}}
							title={filterByText.replace(/[()]/g, "").trim()}
						/>
					)}
				</label>
				<div style={styles.customDropdown} className="custom-dropdown">
					<div
						style={styles.dropdownButton}
						onClick={(e) => {
							e.stopPropagation(); // Prevent event bubbling
							toggleDropdown(filterName);
						}}
					>
						<span
							style={{
								color:
									(selectedFilters[filterName] || []).length === 0
										? "#999"
										: "#333",
								display: "inline-block",
								width: "calc(100% - 20px)",
							}}
						>
							{getFilterDropdownText(filterName, safeOptions)}
						</span>
						<span
							style={{
								float: "right",
								marginTop: "2px",
							}}
						>
							<ChevronDown
								style={{ width: "16px", height: "16px", color: "#666" }}
							/>
						</span>
					</div>{" "}
					{openDropdowns[filterName] && (
						<div
							style={styles.dropdownContent}
							className="dropdown-content"
							onClick={(e) => e.stopPropagation()}
						>
							<div style={styles.searchBox}>
								<input
									type="text"
									placeholder={`Search ${label.toLowerCase()}...`}
									value={searchTerms[filterName] || ""}
									onChange={(e) =>
										handleSearchChange(filterName, e.target.value)
									}
									style={styles.searchInput}
									onClick={(e) => e.stopPropagation()} // Prevent dropdown close on input click
								/>
							</div>
							{/* Select All / Deselect All Button */}
							{filteredOptions.length > 0 && (
								<div
									style={{
										...styles.selectAllButton,
										backgroundColor: isAllSelected ? "#f8f8f8" : "#fff",
									}}
									onClick={(e) => {
										e.stopPropagation();
										if (isAllSelected) {
											handleDeselectAll(filterName);
										} else {
											handleSelectAll(filterName, filteredOptions);
										}
									}}
									onMouseEnter={(e) => {
										e.target.style.backgroundColor = "#f5f5f5";
									}}
									onMouseLeave={(e) => {
										e.target.style.backgroundColor = isAllSelected
											? "#f8f8f8"
											: "#fff";
									}}
								>
									<input
										type="checkbox"
										checked={isAllSelected}
										onChange={() => {}}
										style={styles.checkbox}
									/>
									<span style={{ fontWeight: "600" }}>
										{isAllSelected ? "Deselect All" : "Select All"}
									</span>
								</div>
							)}

							{filteredOptions.map((option) => (
								<div
									key={option}
									style={styles.checkboxItem}
									onClick={(e) => {
										e.stopPropagation(); // Prevent event bubbling
										handleFilterToggle(filterName, option);
									}}
								>
									<input
										type="checkbox"
										checked={(selectedFilters[filterName] || []).includes(
											option,
										)}
										onChange={() => {}}
										style={styles.checkbox}
									/>
									<span>{option}</span>
								</div>
							))}
							{filteredOptions.length === 0 && (
								<div style={styles.noResults}>No results found</div>
							)}
						</div>
					)}
				</div>
			</div>
		);
	};

	// Handle clicking on collapsed sidebar to expand it
	const handleCollapsedSidebarClick = () => {
		if (isSidebarCollapsed) {
			setIsSidebarCollapsed(false);
		}
	};
	const styles = {
		container: {
			width: isSidebarCollapsed ? "50px" : "250px",
			backgroundColor: isSidebarCollapsed && isHovered ? "#e8e8e8" : "#f8f8f8",
			padding: 0,
			transition: "all 0.5s ease",
			position: "relative",
			overflow: "hidden",
			paddingBottom: "10px",
			borderRight: "2px solid black",
			borderTop: "2px solid black",
			height: "100%",
			fontFamily: "MAN EUROPE_Regular",
			cursor: isSidebarCollapsed ? "pointer" : "default",
			display: "flex",
			flexDirection: "column",
		},
		header: {
			display: "flex",
			alignItems: "center",
			padding: "15px",
			backgroundColor: isSidebarCollapsed && isHovered ? "#e8e8e8" : "#f8f8f8",
			justifyContent: isSidebarCollapsed ? "center" : "space-between",
			flexDirection: isSidebarCollapsed ? "column" : "row",
			transition: "background-color 0.3s ease",
			flexShrink: 0,
		},
		title: {
			fontSize: "16px",
			margin: 0,
			display: "flex",
			alignItems: "center",
			fontFamily: "MAN EUROPE_Bold",
		},
		toggleButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			borderRadius: "4px",
			transition: "background-color 0.2s ease",
		},
		content: {
			opacity: isSidebarCollapsed ? 0 : 1,
			visibility: isSidebarCollapsed ? "hidden" : "visible",
			transition: "opacity 0.2s ease",
			width: "100%",
			padding: "10px 4px 10px 15px", // Right padding for scrollbar space
			flex: 1,
			overflowY: hasScrollableContent ? "scroll" : "auto", // Show scrollbar when needed
			overflowX: "hidden",
			maxHeight: "calc(100vh - 74px)", // Account for fixed header height
			minHeight: "300px", // Minimum height to ensure content area
			scrollBehavior: "smooth", // Smooth scrolling
			position: "relative", // For pseudo-elements
			boxSizing: "border-box",
		},
		filterLabel: {
			display: "block",
			fontSize: "14px",
			marginBottom: "6px",
			color: "#333",
			fontFamily: "MAN EUROPE_Bold",
		},
		filterGroup: {
			marginBottom: "15px",
		},
		customDropdown: {
			position: "relative",
			width: "90%",
			marginBottom: "16px",
		},
		dropdownButton: {
			width: "100%",
			padding: "8px",
			border: "1px solid #ddd",
			backgroundColor: "white",
			cursor: "pointer",
			fontFamily: "MAN EUROPE_Regular",
			fontSize: "14px",
			boxSizing: "border-box",
			textAlign: "left",
		},
		dropdownContent: {
			position: "absolute",
			top: "100%",
			left: 0,
			right: 0,
			backgroundColor: "white",
			border: "1px solid #ddd",
			borderTop: "none",
			fontSize: "12px",
			zIndex: 1000,
			maxHeight: "200px",
			overflowY: "auto",
		},
		searchBox: {
			padding: "8px",
		},
		searchInput: {
			width: "100%",
			padding: "6px",
			border: "1px solid #ddd",
			borderRadius: "4px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Regular",
			boxSizing: "border-box",
		},
		checkboxItem: {
			padding: "8px 12px",
			display: "flex",
			alignItems: "center",
			cursor: "pointer",
			fontFamily: "MAN EUROPE_Regular",
		},
		selectAllButton: {
			padding: "8px 12px",
			display: "flex",
			alignItems: "center",
			cursor: "pointer",
			fontFamily: "MAN EUROPE_Regular",
			borderBottom: "1px solid #eee",
			marginBottom: "4px",
			transition: "background-color 0.2s ease",
			"&:hover": {
				backgroundColor: "#f5f5f5",
			},
		},
		checkbox: {
			marginRight: "8px",
		},
		noResults: {
			padding: "8px 12px",
			color: "#999",
			fontStyle: "italic",
			fontSize: "12px",
		},
		errorContainer: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			padding: "20px",
		},
		retryButton: {
			padding: "8px",
			backgroundColor: "#cc0000",
			border: "1px solid #cc0000",
			cursor: "pointer",
			color: "white",
			borderRadius: "4px",
			fontFamily: "MAN EUROPE_Bold",
			marginTop: "10px",
		},
		modalContent: {
			padding: "20px",
		},
		modalFilterGrid: {
			display: "grid",
			gridTemplateColumns: "1fr",
			gap: "20px",
			marginBottom: "20px",
		},
		modalFilterSection: {
			marginBottom: "25px",
		},
	};

	return (
		<div
			className="sidebar-container filter-section"
			data-tour-target="serie-sidebar"
			style={styles.container}
			onClick={handleCollapsedSidebarClick}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			title={isSidebarCollapsed ? "Click to expand filters" : ""}
		>
			<div style={styles.header}>
				{!isSidebarCollapsed && (
					<h2 style={styles.title}>
						<span
							style={{
								marginLeft: "8px",
								fontSize: "25px",
								fontFamily: "MAN EUROPE_Bold",
								fontWeight: "normal",
							}}
						>
							Filters
						</span>
					</h2>
				)}
				<button
					style={styles.toggleButton}
					onClick={(e) => {
						e.stopPropagation();
						setIsSidebarCollapsed(!isSidebarCollapsed);
					}}
					title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
				>
					{isSidebarCollapsed ? (
						<ChevronRight style={{ width: "20px", height: "20px" }} />
					) : (
						<ChevronLeft style={{ width: "20px", height: "20px" }} />
					)}
				</button>
			</div>
			<div
				ref={contentRef}
				className="serie-sidebar-content"
				style={styles.content}
			>
				{isLoading ? (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							padding: "20px",
						}}
					>
						<CircularProgress size={24} sx={{ color: "#cc0000" }} />
						<Typography
							variant="body2"
							sx={{ marginTop: "10px", color: "#666" }}
						>
							Loading filters...
						</Typography>
					</div>
				) : error ? (
					<div style={styles.errorContainer}>
						<Typography
							variant="body2"
							sx={{ color: "red", textAlign: "center" }}
						>
							{error}
						</Typography>
						<button
							style={styles.retryButton}
							onClick={() => window.location.reload()}
						>
							Retry
						</button>
					</div>
				) : (
					<>
						{" "}
						{/* Programm Filter */}
						{renderCustomDropdown("programm", programmValues, "Programm")}
						{/* Project No Filter */}
						{renderCustomDropdown("projectNo", projectNumbers, "Project No")}{" "}
						{/* ALG Filter – dynamic: scoped to selected Programm / Project No */}
						{renderCustomDropdown("alg", effectiveAlgValues, "ALG", true)}{" "}
						{/* Change No Filter – dynamic: scoped to selected Programm / Project No */}
						{renderCustomDropdown(
							"changeNo",
							effectiveChangeNumbers,
							"Change No",
							true,
						)}{" "}
						{/* Se-Team Filter – dynamic: scoped to selected Programm / Project No */}
						{renderCustomDropdown(
							"seTeam",
							effectiveSeTeamValues,
							"Se-Team",
							true,
						)}
						{/* BZA Filter – dynamic: scoped to selected Programm / Project No */}
						{renderCustomDropdown("bza", effectiveBzaValues, "BZA", true)}
						{/* Part No Prefix Filter – static prefix list, filters on st.snr */}
						{renderCustomDropdown(
							"partNoPrefix",
							PART_NO_PREFIXES,
							"Part No Prefix",
						)}
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: "10px",
								width: "90%",
								marginTop: "20px",
							}}
						>
							<button
								onClick={toggleModal}
								data-tour-target="additional-filters-toggle"
								style={{
									padding: "8px",
									backgroundColor: isModalOpen ? "#cc0000" : "#545454",
									color: "white",
									border: "none",
									borderRadius: "0px",
									cursor: "pointer",
									fontFamily: "MAN EUROPE_Bold",
									fontSize: "14px",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "8px",
									transition: "background-color 0.3s ease",
								}}
								title={
									isModalOpen
										? "Close additional filters"
										: "Open additional filters"
								}
							>
								Additional Filters
								<ChevronRight
									style={{
										width: "16px",
										height: "16px",
										transform: isModalOpen ? "rotate(90deg)" : "rotate(0deg)",
										transition: "transform 0.3s ease",
									}}
								/>
							</button>
							<button
								onClick={applyFilters}
								style={{
									padding: "8px",
									backgroundColor: "#cc0000",
									border: "1px solid #cc0000",
									cursor: "pointer",
									color: "white",
									borderRadius: "0px",
									fontFamily: "MAN EUROPE_Bold",
								}}
							>
								Apply Filters
							</button>

							<button
								onClick={clearFilters}
								style={{
									padding: "8px",
									backgroundColor: "#1e2832",
									border: "1px solid #ddd",
									cursor: "pointer",
									color: "#fff",
									borderRadius: "0px",
									fontFamily: "MAN EUROPE_Bold",
								}}
							>
								Clear all Filters
							</button>
						</div>
					</>
				)}
			</div>
			{renderAdditionalFiltersModal()}
		</div>
	);
};

export default SerieSidebar;
